import { Box, Center, Spinner, useToast } from '@chakra-ui/react';
import HotTable from '@handsontable/react';
import Handsontable from 'handsontable';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { useTranslation } from 'helpers/languages/translations';
import { useAddListDataMutation, useUpdateListDataMutation } from 'core/API/listApi';
import { GLOBAL_TEMPLATE_PROPS } from 'pages/ListManagement/const';
import { CellChange, ChangeSource, IError, SourceData, TableProps } from 'pages/ListManagement/types';
import React, { useEffect, useRef, useState } from 'react';
import { cloneDeep, keyBy } from 'lodash';
import { checkRequestStatus } from 'utils/erros';
import { MDataToolBar } from './MDataToolBar';
import { translateHeaders } from './ListTemplates/helpers/HeadersList';
import sanitizeHtml from 'sanitize-html';

const Grid: React.FC<TableProps> = ({
    toolId,
    children,
    headers,
    list,
    afterChange,
    beforeValidate,
    afterValidate,
    beforeDownload,
    beforeUpload,
    afterLoadData,
    afterBeginEditing,
    afterPaste,
    paramHotRef,
    hasErrors,
}) => {
    const hotRef = paramHotRef || useRef<React.ElementRef<typeof HotTable> | null>(null);
    const toast = useToast();
    const translate = useTranslation();
    const companyInfo = useGetCompanyInfoSelector();
    const [newId, setNewId] = useState<number>(list.id);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [isTableValid, setIsTableValid] = useState<boolean>(true);
    const [updateListData, { isLoading: isUpdateListLoading }] = useUpdateListDataMutation();
    const [addListData, { isLoading: isAddListLoading }] = useAddListDataMutation();
    const [templateData, setData] = useState<SourceData>();
    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(22);

    const validateCells = () => {
        return new Promise((resolve) => {
            const hot = hotRef.current?.hotInstance;

            hot?.validateCells((valid) => {
                setIsTableValid(valid);
                hot.render();
                resolve(valid);
            });
        });
    };

    const createButton = (row: number, callback: (data: string[][], rowCallBack: number) => void, name: string) => {
        const button = document.createElement('button');
        button.style.margin = '2px';
        button.innerHTML = sanitizeHtml(name);

        button.onclick = () => {
            const hot = hotRef?.current?.hotInstance;
            if (hot) {
                const data = [...hot?.getData()] as string[][];
                callback(data, row);
            }
        };

        return button;
    };

    const onClickAddRow = (data: string[][], row: number) => {
        const hot = hotRef?.current?.hotInstance;
        hot?.alter('insert_row_below', row, 1);
        headers.forEach((h, i) => hot?.setDataAtCell(row + 1, i, ''));
    };

    const onClickDelRow = (data: string[][], row: number) => {
        const hot = hotRef?.current?.hotInstance;
        hot?.alter('remove_row', row, 1);
    };

    type DrawCheckboxInRowHeaders = (this: Handsontable, row: number, TH: HTMLTableCellElement) => void;

    const drawCheckboxInRowHeaders: DrawCheckboxInRowHeaders = function drawCheckboxInRowHeaders(this, row, TH) {
        const div = document.createElement('div');

        const add = createButton(row, onClickAddRow, '+');
        const minus = createButton(row, onClickDelRow, '-');

        div.appendChild(add);
        div.appendChild(minus);

        Handsontable.dom.empty(TH);

        TH.appendChild(div);
    };

    const cleanTable = (data: SourceData) => {
        return data
            .map((row) => row.map((value) => (value || value === 0 ? value : '')))
            .filter((row) => row.flat().filter((rowData) => rowData).length);
    };

    const saveClickCallback = async () => {
        try {
            if (!hotRef.current) {
                throw new Error(translate(`error.generic`) as string);
            }
            const hot = hotRef.current.hotInstance;
            let newData = hot?.getData() as SourceData;

            let newList = { ...list, data: newData, columns: headers };
            if (beforeValidate) {
                beforeValidate(newList);
            }

            if (hasErrors?.current) {
                toast({
                    title: translate(`list.edit.error`),
                    status: 'error',
                    isClosable: true,
                });
                return;
            }

            if (!(await validateCells())) {
                toast({
                    title: translate(`list.edit.error`),
                    status: 'error',
                    isClosable: true,
                });
            } else {
                if (newData) {
                    newData = cleanTable(newData);
                    newList = { ...newList, data: newData };
                    if (afterValidate) {
                        newList = afterValidate(newList);
                    }
                    const res =
                        newId === 0
                            ? await addListData({
                                  idTool: toolId,
                                  lists: { ...newList, upc_type: companyInfo ? companyInfo?.upc_type : '0' },
                              })
                            : await updateListData({ idTool: toolId, list: { ...newList, id: newId } });

                    if ((res as any).data?.lists?.data?.id) {
                        setNewId((res as any).data?.lists?.data?.id);
                    }

                    checkRequestStatus(res, translate(`list.edit.error`) as string);
                    setIsDirty(false);
                    toast({
                        title: translate(`list.edit.success`),
                        status: 'success',
                        isClosable: true,
                    });
                }
            }
        } catch (e) {
            const err = e as IError;
            toast({
                title: err.message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const onAfterChange = (changes: CellChange[] | null, source: ChangeSource) => {
        if (afterChange) {
            afterChange(changes, source, hotRef);
        }

        if (changes) {
            setIsDirty(true);
        }

        validateCells();
    };

    useEffect(() => {
        if (!templateData) {
            setData(cloneDeep(list.data));
        }
    }, [templateData]);

    const afterGetColHeader = (col: number, TH: HTMLTableCellElement) => {
        const sortingPlugin = hotRef?.current?.hotInstance?.getPlugin('columnSorting');
        if (!sortingPlugin) return;
        const headerContent = TH.querySelector('.colHeader');

        if (!headerContent) return;

        const existingIcon = TH.querySelector('.sort-icon');
        if (existingIcon) {
            existingIcon.remove();
        }

        // @ts-ignore
        const state = sortingPlugin.getSortConfig()?.find((cfg) => cfg.column === col);

        const icon = document.createElement('span');
        icon.className = 'sort-icon';

        if (!state && col !== -1) {
            icon.textContent = ' ⇅';
        }

        headerContent.appendChild(icon);
    };

    return (
        <Box>
            {templateData ? (
                <>
                    <MDataToolBar
                        hotRef={hotRef}
                        toolId={toolId}
                        list={list}
                        headers={headers}
                        beforeDownload={beforeDownload}
                        setIsDirty={setIsDirty}
                        setIsTableValid={setIsTableValid}
                        setData={setData}
                        beforeUpload={beforeUpload}
                        isDirty={isDirty}
                        isTableValid={isTableValid}
                        disableSave={list.name === ''}
                        saveClickCallback={saveClickCallback}
                        readonly={readonly}
                    />
                    <HotTable
                        ref={hotRef}
                        afterPaste={afterPaste}
                        afterGetRowHeader={drawCheckboxInRowHeaders}
                        colHeaders={translateHeaders(headers)}
                        afterLoadData={afterLoadData}
                        afterGetColHeader={afterGetColHeader}
                        afterBeginEditing={afterBeginEditing}
                        data={templateData}
                        {...GLOBAL_TEMPLATE_PROPS}
                        afterChange={(changes: CellChange[] | null, source: ChangeSource) => {
                            setIsDirty(true);
                            onAfterChange(changes, source);
                        }}
                        licenseKey="non-commercial-and-evaluation"
                        readOnly={readonly}
                        columnSorting={true}
                    >
                        {children}
                    </HotTable>
                </>
            ) : (
                <Center w="100%" h="200px" bg="white" border="1px solid" borderColor="gray.100">
                    <Spinner />
                </Center>
            )}
        </Box>
    );
};

export default Grid;
